import * as React from 'react'
import styled, { css } from 'styled-components'
import { HelpCenter, HelpCenterArticle, HelpCenterMessengerEvent, HelpCenterMessengerEventType, PublicLocale, WidgetMessengerEventType } from '../../../types'
import Container from '../../components/Messenger/Container'
import Navigation from '../../components/Messenger/Navigation'
import BackButton from '../../components/Messenger/Navigation/BackButton'
import MaximizeToggleButton from '../../components/Messenger/Navigation/MaximizeToggleButton'
import TopNavigation from '../../components/Messenger/TopNavigation'
import { MessageHelper, WidgetHelper } from '../../helpers'
import { HelpCenterController } from '../../controllers'
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom'
import { connect, useSelector } from 'react-redux'
import { AppState } from '../../store'
import ArticleSkeleton from '../../../help_center/components/Skeleton/ArticleSkeleton'
import HelpCenterHelper from '../../../helpers/HelpCenterHelper'
import ScrollToTopOnMount from '../../effects/ScrollToTopOnMount'

const Wrapper = styled.div<{ scrolled?: boolean, iframeLoaded?: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  flex: 1;
  flex-grow: 1;

  ${props => props.scrolled && css`
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  `}

  iframe {
    border: none;
    width: 100%;
    height: 100%;
    flex: 1;
    display: none;
    z-index: 999;

    ${props => props.iframeLoaded && css`
      display: block;
    `}
  }
`

type IProps = RouteComponentProps<{ id: string }> & {
  locale: PublicLocale
  mobile: boolean
}
interface IState {
  scrolled: boolean
  helpCenter: HelpCenter
  article: HelpCenterArticle
  didInitialLoad: boolean
  iframeLoaded: boolean
}

class Article extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      scrolled: false,
      helpCenter: null,
      article: null,
      didInitialLoad: false,
      iframeLoaded: false,
    }

    this.fetchArticle = this.fetchArticle.bind(this)
    this.onMessageReceive = this.onMessageReceive.bind(this)
  }

  componentDidMount(): void {
    window.addEventListener('message', this.onMessageReceive)
    MessageHelper.sendMessage({ type: WidgetMessengerEventType.MAXIMIZE_WINDOW })
    this.fetchArticle()
  }

  componentWillUnmount(): void {
    window.removeEventListener('message', this.onMessageReceive)
    MessageHelper.sendMessage({ type: WidgetMessengerEventType.MINIMIZE_WINDOW })
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.fetchArticle()
    }
  }

  async fetchArticle() {
    const { match: { params: { id } } } = this.props
    try {
      this.setState({ didInitialLoad: false, iframeLoaded: false })
      const { help_center, article } = await HelpCenterController.getArticle(id)

      this.setState({
        helpCenter: help_center,
        article: article,
        didInitialLoad: true,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  onMessageReceive(event: MessageEvent<HelpCenterMessengerEvent>) {
    console.log('Received message:', event.data);
    if (!event?.data?.type) return
    const { history } = this.props

    switch (event.data.type) {
      case HelpCenterMessengerEventType.DEEP_LINK:
        history.push(WidgetHelper.deepLink(event.data.url))
        break
      case HelpCenterMessengerEventType.ARTICLE_LOADED:
        setTimeout(() => this.setState({ iframeLoaded: true }), 250)
        break
    }
  }

  render() {
    const { locale, mobile } = this.props
    const { scrolled, helpCenter, article, didInitialLoad, iframeLoaded } = this.state

    return (
      <Container>
        <Navigation
          left={<BackButton />}
          title=''
          right={!mobile ? <MaximizeToggleButton /> : null}
          themed={false}
        />
        <ScrollToTopOnMount />

        <TopNavigation />

        <Wrapper scrolled={scrolled} iframeLoaded={iframeLoaded}>
          {!iframeLoaded && <ArticleSkeleton margins />}
          {didInitialLoad && <iframe
            key={article.id}
            src={HelpCenterHelper.getArticleUrl(helpCenter, article, locale, { embed: true })}
          />}
        </Wrapper>
      </Container>
    )
  }
}

const mapStatetoProps = (state: AppState) => ({
  locale: state.application.locale,
  mobile: state.application.hostMobile,
})

export default connect(mapStatetoProps, null)(Article)